import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Mail, Lock, ArrowRight, Loader2, LogIn } from "lucide-react";
import { authLogin, authRegister, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Google icon (multi-color) as inline SVG so we don't need extra deps.
 */
function GoogleGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

export default function LoginPage({ mode = "login" }) {
  const [tab, setTab] = useState(mode); // 'login' | 'register'
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const { loginWith, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let data;
      if (tab === "login") {
        data = await authLogin(form.email, form.password);
        toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
      } else {
        if (!form.name.trim()) throw new Error("Please enter your name or business name.");
        if (form.password.length < 6) throw new Error("Password must be at least 6 characters.");
        data = await authRegister({
          email: form.email,
          password: form.password,
          name: form.name.trim(),
        });
        toast.success("Account created — welcome!");
      }
      loginWith(data.token, data.user);
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setTab("login");
    setForm({ email: "demo@gohigher.com", password: "Demo!Client2026", name: "" });
    toast.info("Demo credentials filled. Click Sign in.");
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const continueWithGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div className="min-h-screen bg-gh-navy text-white flex flex-col">
      {/* Top nav */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center shadow-lg shadow-emerald-500/30">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-bold tracking-tight">
              Go <span className="text-[#D4AF37]">Higher</span>
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-white/60 hover:text-white transition-colors"
            data-testid="login-back-home"
          >
            ← Back to site
          </Link>
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-2">
        {/* Left */}
        <div className="hidden lg:flex flex-col justify-between p-12 border-r border-white/5 bg-white/[0.015] relative overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 opacity-30 -z-0"
            style={{
              background:
                "radial-gradient(500px 300px at 30% 30%, rgba(16,185,129,0.15), transparent 60%), radial-gradient(400px 250px at 70% 80%, rgba(212,175,55,0.10), transparent 60%)",
            }}
          />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
              Client Portal
            </div>
            <h1 className="mt-3 font-display text-4xl font-bold leading-tight max-w-md">
              Track every removal, in <span className="text-[#10B981]">real time.</span>
            </h1>
            <p className="mt-5 text-white/60 max-w-md leading-relaxed">
              Watch your review cases move through filing, escalation, and confirmed removal — and
              only pay for the ones Google actually removes.
            </p>
          </div>
          <div className="relative rounded-2xl border border-white/10 bg-[#0d1424]/70 p-5 max-w-sm">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">
              Demo login
            </div>
            <div className="mt-2 text-sm text-white/80">
              Explore the dashboard with a pre-seeded client account (5 sample cases).
            </div>
            <button
              type="button"
              onClick={fillDemo}
              data-testid="login-fill-demo"
              className="mt-3 text-sm inline-flex items-center gap-1 font-semibold text-[#10B981] hover:text-[#0ea774] transition-colors"
            >
              Use demo credentials <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Right - form */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1 mb-6">
              {[
                { k: "login", label: "Sign in" },
                { k: "register", label: "Create account" },
              ].map((t) => (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`login-tab-${t.k}`}
                  className={`px-4 h-8 rounded-full text-xs font-semibold transition-colors ${
                    tab === t.k ? "bg-[#10B981] text-white" : "text-white/60 hover:text-white"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
              {tab === "login" ? "Welcome back." : "Get your dashboard."}
            </h2>
            <p className="mt-2 text-white/55 text-sm">
              {tab === "login"
                ? "Sign in to see your review cases and progress."
                : "Sign up to track your removal cases and pay only after confirmed removals."}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
              <button
                type="button"
                onClick={continueWithGoogle}
                data-testid="login-google"
                className="group w-full inline-flex items-center justify-center gap-2.5 h-12 rounded-full border border-white/15 hover:border-white/30 bg-white/[0.02] hover:bg-white/[0.05] text-white text-sm font-semibold transition-colors"
              >
                <GoogleGlyph />
                Continue with Google
              </button>

              <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-white/30 font-semibold">
                <div className="flex-1 h-px bg-white/10" />
                or use email
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {tab === "register" && (
                <Field label="Your name or business name" icon={<ShieldCheck className="w-4 h-4" />}>
                  <input
                    className="input"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Riverside Bistro"
                    data-testid="login-name"
                  />
                </Field>
              )}
              <Field label="Email" icon={<Mail className="w-4 h-4" />}>
                <input
                  type="email"
                  className="input"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@business.com"
                  data-testid="login-email"
                  required
                />
              </Field>
              <Field label="Password" icon={<Lock className="w-4 h-4" />}>
                <input
                  type="password"
                  className="input"
                  value={form.password}
                  onChange={onChange("password")}
                  placeholder="At least 6 characters"
                  data-testid="login-password"
                  required
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white font-semibold text-sm transition-colors shadow-lg shadow-emerald-500/30 disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {tab === "login" ? "Signing in…" : "Creating account…"}
                  </>
                ) : (
                  <>
                    {tab === "login" ? <LogIn className="w-4 h-4" /> : null}
                    {tab === "login" ? "Sign in" : "Create account"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="mt-6 text-xs text-white/40 text-center">
              By continuing you agree to Go Higher's Terms of Service and Privacy Policy.
            </p>
          </motion.div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          color: white;
          border-radius: 10px;
          padding: 12px 14px 12px 40px;
          font-size: 14px;
          transition: border-color .15s, background-color .15s, box-shadow .15s;
          outline: none;
        }
        .input::placeholder { color: rgba(255,255,255,0.35); }
        .input:focus {
          border-color: rgba(16,185,129,0.6);
          background: rgba(16,185,129,0.05);
          box-shadow: 0 0 0 3px rgba(16,185,129,0.15);
        }
      `}</style>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
        {label}
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none">
          {icon}
        </span>
        {children}
      </div>
    </label>
  );
}
